import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Mail, Phone, MapPin, GraduationCap, Ruler, Weight, Trophy, Youtube } from "lucide-react";

function embedUrl(url) {
  if (!url) return null;
  if (url.includes("youtube.com/watch?v=")) return url.replace("watch?v=", "embed/");
  if (url.includes("youtu.be/")) return url.replace("youtu.be/", "youtube.com/embed/");
  if (url.includes("hudl.com")) return url; // may not embed directly, we'll iframe anyway
  return url;
}

export default function PlayerPublic() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    api.get(`/players/${id}`).then(r => setP(r.data));
    api.get(`/videos?player_id=${id}`).then(r => setVideos(r.data));
  }, [id]);

  if (!p) return <div className="section-dark min-h-screen flex items-center justify-center text-white/60">Loading…</div>;

  return (
    <div>
      <section className="section-dark py-20 relative overflow-hidden">
        <div className="container-app relative z-10 grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-1">
            <div className="aspect-square bg-ink-2 border border-white/10 overflow-hidden">
              {p.photo_url ? <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-heading text-6xl">{(p.name||"?").split(" ").map(s=>s[0]).join("")}</div>}
            </div>
          </div>
          <div className="md:col-span-2">
            <div className="label-tag mb-3">Player Profile</div>
            <h1 className="font-heading font-black uppercase text-5xl md:text-7xl leading-none">{p.name}</h1>
            <div className="mt-4 flex flex-wrap gap-4 text-white/70">
              {p.position && <span className="font-mono text-sm px-3 py-1 border border-white/15">{p.position}</span>}
              {p.class_year && <span className="font-mono text-sm px-3 py-1 border border-white/15">Class of {p.class_year}</span>}
              {p.recruiting_status && <span className="font-mono text-sm px-3 py-1 border border-[color:var(--gold)] text-[color:var(--gold)]">{p.recruiting_status.toUpperCase()}</span>}
            </div>
            {p.bio && <p className="mt-6 text-white/75 leading-relaxed max-w-2xl">{p.bio}</p>}
            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat icon={<Ruler size={16}/>} label="Height" v={p.height} />
              <Stat icon={<Weight size={16}/>} label="Weight" v={p.weight} />
              <Stat icon={<GraduationCap size={16}/>} label="GPA" v={p.gpa} />
              <Stat icon={<MapPin size={16}/>} label="Location" v={p.location} />
            </div>
          </div>
        </div>
      </section>

      <section className="section-light py-16">
        <div className="container-app grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2">
            <div className="label-tag mb-3" style={{ color: "#000" }}>Film & Highlights</div>
            <h2 className="font-heading font-black uppercase text-3xl md:text-4xl mb-8">Watch the tape.</h2>
            {videos.length === 0 && p.hudl_url && (
              <a href={p.hudl_url} target="_blank" rel="noreferrer" className="btn-ghost-dark inline-flex" data-testid="hudl-link"><Youtube size={16}/> View on Hudl</a>
            )}
            <div className="space-y-6">
              {videos.map(v => (
                <div key={v.id} className="card-light p-4" data-testid={`player-video-${v.id}`}>
                  <div className="font-heading uppercase font-bold text-sm mb-3">{v.title}</div>
                  <div className="aspect-16-9 bg-black">
                    <iframe title={v.title} src={embedUrl(v.url)} allowFullScreen frameBorder="0" />
                  </div>
                </div>
              ))}
              {videos.length === 0 && !p.hudl_url && <div className="text-neutral-400">No film uploaded yet.</div>}
            </div>
          </div>
          <div className="space-y-6">
            <div className="card-light p-6">
              <div className="label-tag mb-3" style={{ color: "#000" }}>School</div>
              <div className="font-heading uppercase font-bold text-xl">{p.school || "—"}</div>
            </div>
            <div className="card-light p-6">
              <div className="label-tag mb-3" style={{ color: "#000" }}>Contact</div>
              {p.email && <div className="flex items-center gap-2 text-sm mb-2"><Mail size={14}/>{p.email}</div>}
              {p.phone && <div className="flex items-center gap-2 text-sm"><Phone size={14}/>{p.phone}</div>}
            </div>
            {p.awards?.length > 0 && (
              <div className="card-light p-6">
                <div className="label-tag mb-3" style={{ color: "#000" }}>Awards</div>
                <ul className="space-y-2">
                  {p.awards.map((a,i) => <li key={i} className="flex items-center gap-2 text-sm"><Trophy size={14} className="text-[color:var(--gold)]"/> {a}</li>)}
                </ul>
              </div>
            )}
            <Link to="/players" className="btn-ghost-dark w-full justify-center" data-testid="back-to-directory">Back to Directory</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon, label, v }) {
  return (
    <div className="border border-white/10 p-4 bg-white/[0.02]">
      <div className="flex items-center gap-2 text-white/50 text-xs uppercase tracking-widest">{icon}{label}</div>
      <div className="mt-2 font-heading font-black text-2xl text-[color:var(--gold)]">{v || "—"}</div>
    </div>
  );
}
